import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PacModeBanner } from "@/components/ops/PacModeBanner";
import {
  BarChart3, Download, RefreshCw, Calculator, FileSpreadsheet,
  FileText, ChevronDown, Info,
} from "lucide-react";

function MonthPicker({ year, month, onChange }) {
  return (
    <div className="flex gap-2">
      <select
        data-testid="select-year"
        value={year}
        onChange={e => onChange(Number(e.target.value), month)}
        className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
      >
        {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
      </select>
      <select
        data-testid="select-month"
        value={month}
        onChange={e => onChange(year, Number(e.target.value))}
        className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
      >
        {["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"]
          .map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
      </select>
    </div>
  );
}

function IvaCard({ data }) {
  if (!data) return null;
  const fmt = v => (v == null
    ? <span className="text-slate-400 italic text-xs">no disponible</span>
    : `$${v.toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN`);
  const items = [
    { label: "IVA causado (facturas emitidas)", value: data.iva_causado, color: "text-red-600" },
    { label: "IVA acreditable (facturas recibidas)", value: data.iva_acreditable, color: "text-green-600" },
    { label: "IVA retenido a terceros", value: data.iva_retenido_terceros, color: "text-slate-600" },
    { label: "ISR retenido a terceros", value: data.isr_retenido_terceros, color: "text-slate-600" },
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3">
      {items.map(it => (
        <div key={it.label} className="flex justify-between items-center py-2 border-b border-slate-100 last:border-0">
          <span className="text-sm text-slate-600">{it.label}</span>
          <span className={`font-mono font-semibold text-sm ${it.color}`}>{fmt(it.value)}</span>
        </div>
      ))}
      <div className="bg-slate-900 rounded-xl p-4 flex justify-between items-center mt-2">
        <span className="text-sm font-medium text-slate-200">IVA a pagar (vence {data.due_date})</span>
        <span className="text-xl font-bold text-white font-mono">
          ${(data.iva_a_pagar || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })} MXN
        </span>
      </div>
      <p className="text-xs text-slate-400">
        Periodo {data.period?.from} al {data.period?.to_exclusive} (excluye el dia final).
        Basado en {data.facturas_emitidas || 0} facturas emitidas y {data.facturas_recibidas || 0} facturas de proveedor.
        {data.retenciones_disponibles === false && " Las facturas del periodo no traen datos de retenciones."}
      </p>
    </div>
  );
}

export default function OpsFiscalDeclaraciones() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState("");
  const [ivaData, setIvaData] = useState(null);
  const [loadingIva, setLoadingIva] = useState(false);
  const [downloading, setDownloading] = useState({ diot: false, balanza: false });

  useEffect(() => {
    api.get("/ops/legal-entities").then(res => {
      const ents = res.data;
      const arr = Array.isArray(ents) ? ents : ents.entities || ents.legal_entities || [];
      setEntities(arr);
      if (arr.length > 0) setSelectedEntity(arr[0].id);
    });
  }, []);

  const handleMonthChange = (y, m) => { setYear(y); setMonth(m); setIvaData(null); };

  const calcIva = async () => {
    if (!selectedEntity) return toast.error("Selecciona una entidad legal");
    setLoadingIva(true);
    try {
      const res = await api.get(`/ops/fiscal/reports/iva-mensual?year=${year}&month=${month}&legal_entity_id=${selectedEntity}`);
      setIvaData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al calcular IVA");
    } finally {
      setLoadingIva(false);
    }
  };

  const downloadDiot = async () => {
    if (!selectedEntity) return toast.error("Selecciona una entidad legal");
    setDownloading(d => ({ ...d, diot: true }));
    try {
      const res = await api.get(`/ops/fiscal/reports/diot?year=${year}&month=${month}&legal_entity_id=${selectedEntity}&format=txt`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/plain" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `DIOT_${year}_${String(month).padStart(2, "0")}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("DIOT descargado");
    } catch (e) {
      toast.error("Error al generar DIOT");
    } finally {
      setDownloading(d => ({ ...d, diot: false }));
    }
  };

  const downloadBalanza = async () => {
    if (!selectedEntity) return toast.error("Selecciona una entidad legal");
    setDownloading(d => ({ ...d, balanza: true }));
    try {
      const res = await api.get(`/ops/fiscal/reports/balanza?year=${year}&month=${month}&legal_entity_id=${selectedEntity}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Balanza_${year}_${String(month).padStart(2, "0")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Balanza descargada");
    } catch (e) {
      toast.error("Error al generar balanza");
    } finally {
      setDownloading(d => ({ ...d, balanza: false }));
    }
  };

  const monthName = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"][month - 1];

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
      <PacModeBanner />
      <div>
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-600" />
          Declaraciones SAT
        </h2>
        <p className="text-sm text-slate-500 mt-0.5">DIOT, balanza de comprobacion y calculo de IVA mensual</p>
      </div>

      {/* Filtros globales */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex flex-wrap gap-4 items-end">
        <div>
          <label className="text-xs text-slate-600 mb-1 block">Entidad legal</label>
          <select
            data-testid="select-entity-declaraciones"
            value={selectedEntity}
            onChange={e => setSelectedEntity(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
          >
            {entities.map(e => (
              <option key={e.id} value={e.id}>{e.legal_name} ({e.rfc})</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-600 mb-1 block">Periodo</label>
          <MonthPicker year={year} month={month} onChange={handleMonthChange} />
        </div>
      </div>

      {/* Cards de reportes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* IVA Mensual */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100">
            <div className="flex items-center gap-2 mb-1">
              <Calculator className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-slate-800 text-sm">Calculo IVA mensual</h3>
            </div>
            <p className="text-xs text-slate-500">{monthName} {year}</p>
          </div>
          <div className="p-5 space-y-4">
            {ivaData ? (
              <IvaCard data={ivaData} />
            ) : (
              <div className="text-center py-6 text-slate-400">
                <Calculator className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Haz clic en calcular para ver el IVA del periodo</p>
              </div>
            )}
            <button
              data-testid="btn-calcular-iva"
              onClick={calcIva}
              disabled={loadingIva}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loadingIva && <RefreshCw className="w-4 h-4 animate-spin" />}
              {loadingIva ? "Calculando..." : "Calcular IVA"}
            </button>
          </div>
        </div>

        {/* DIOT */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-5 h-5 text-slate-600" />
              <h3 className="font-semibold text-slate-800 text-sm">DIOT</h3>
            </div>
            <p className="text-xs text-slate-500">Declaracion Informativa de Operaciones con Terceros</p>
          </div>
          <div className="p-5 space-y-3">
            <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-600 space-y-1">
              <p>Formato TXT para subir al portal del SAT.</p>
              <p>Incluye todos los proveedores con operaciones del periodo: IVA acreditable, retenido, ISR retenido.</p>
            </div>
            <button
              data-testid="btn-descargar-diot"
              onClick={downloadDiot}
              disabled={downloading.diot}
              className="w-full border border-slate-300 hover:border-slate-400 rounded-lg px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 text-slate-700 disabled:opacity-50"
            >
              {downloading.diot
                ? <><RefreshCw className="w-4 h-4 animate-spin" />Generando...</>
                : <><Download className="w-4 h-4" />Descargar DIOT {monthName} {year}</>
              }
            </button>
          </div>
        </div>

        {/* Balanza */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-100">
            <div className="flex items-center gap-2 mb-1">
              <FileSpreadsheet className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold text-slate-800 text-sm">Balanza de comprobacion</h3>
            </div>
            <p className="text-xs text-slate-500">Excel — ingresos, egresos, IVA</p>
          </div>
          <div className="p-5 space-y-3">
            <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-600 space-y-1">
              <p>Excel con 2 hojas: Ingresos (facturas emitidas) y Egresos (facturas de proveedor).</p>
              <p>Pensado para entregarselo al contador para revision y contabilidad electronica.</p>
            </div>
            <button
              data-testid="btn-descargar-balanza"
              onClick={downloadBalanza}
              disabled={downloading.balanza}
              className="w-full bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-2.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {downloading.balanza
                ? <><RefreshCw className="w-4 h-4 animate-spin" />Generando...</>
                : <><Download className="w-4 h-4" />Descargar Balanza {monthName} {year}</>
              }
            </button>
          </div>
        </div>

        {/* Nota sobre complemento de pago — NO implementado */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden opacity-90">
          <div className="p-5 border-b border-slate-100">
            <div className="flex items-center gap-2 mb-1">
              <Info className="w-5 h-5 text-slate-400" />
              <h3 className="font-semibold text-slate-800 text-sm">Complementos de pago (PPD)</h3>
              <span data-testid="badge-complemento-proximamente" className="text-[10px] font-bold uppercase bg-slate-100 text-slate-500 border border-slate-200 rounded px-1.5 py-0.5">
                Próximamente
              </span>
            </div>
            <p className="text-xs text-slate-500">CFDI tipo P — parcialidades</p>
          </div>
          <div className="p-5 space-y-3">
            <p className="text-xs text-slate-600">
              Si tus facturas son <strong>PPD</strong> (pago en parcialidades), cada pago recibido
              requiere un CFDI tipo P. Esta funcionalidad <strong>todavía no está disponible</strong>:
              el timbrado del complemento de pago no está implementado, así que no se puede emitir
              desde la plataforma.
            </p>
            <button
              data-testid="btn-complemento-pago"
              disabled
              className="w-full border border-slate-200 rounded-lg px-4 py-2.5 text-sm font-medium text-slate-400 bg-slate-50 cursor-not-allowed"
            >
              Emitir complemento de pago (próximamente)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
